import { useState } from "react";
import { BUSINESS } from "../../../mocks/businessData";
import { useScrollReveal } from "../../../hooks/useScrollReveal";

const FORM_URL = "https://readdy.ai/api/form/d72m8hpsakbatd8b3n8g";

export default function QuoteFormSection() {
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const ref = useScrollReveal();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new URLSearchParams();
    const inputs = form.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(
      "input, select, textarea"
    );
    let valid = true;
    inputs.forEach((el) => {
      if (el.required && !el.value.trim()) valid = false;
      if (el.name) formData.append(el.name, el.value);
    });
    if (!valid) return;
    setStatus("sending");
    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: formData.toString(),
      });
      setStatus(res.ok ? "success" : "error");
      if (res.ok) form.reset();
    } catch {
      setStatus("error");
    }
  };

  return (
    <section id="quote-form" className="bg-stone-950 relative overflow-hidden" ref={ref}>
      {/* Subtle pattern */}
      <div
        className="absolute inset-0 opacity-[0.025] pointer-events-none"
        style={{
          backgroundImage:
            "repeating-linear-gradient(45deg, #d97706 0, #d97706 1px, transparent 0, transparent 50%)",
          backgroundSize: "16px 16px",
        }}
      />

      <div className="relative z-10 max-w-6xl mx-auto px-6 py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* ── Left — Premium copy ── */}
          <div>
            <div className="reveal">
              <p className="text-amber-500 text-xs tracking-[0.40em] uppercase font-semibold mb-4">
                Begin Your Transformation
              </p>
              <h2 className="font-serif font-light text-4xl md:text-5xl text-white mb-5 leading-[1.08]">
                Request Your
                <span className="block italic text-amber-300 mt-1">Complimentary Estimate</span>
              </h2>
              <div className="gold-rule mb-7" />
              <p className="text-stone-400 text-sm leading-relaxed mb-10">
                No two projects are identical — and we&apos;d never treat yours like just another job.
                Every estimate is personal, accurate, and completely free.
              </p>
            </div>

            {/* Contact info */}
            <div className="reveal reveal-delay-1 space-y-4 mb-10">
              {[
                { icon: "ri-phone-fill",   label: "Direct Line",    value: BUSINESS.phone, href: BUSINESS.phoneHref },
                { icon: "ri-time-fill",    label: "Availability",   value: "Open 24/7 — Including Weekends & Emergencies" },
                { icon: "ri-map-pin-fill", label: "Coverage",       value: "NJ · NY · PA · CT — or Ship Your Items" },
                { icon: "ri-truck-fill",   label: "Pickup",         value: "Free Professional Pickup & Delivery — No Trip Charge" },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-amber-700/20 border border-amber-700/30 rounded-sm flex-shrink-0">
                    <i className={`${item.icon} text-amber-500 text-sm`} />
                  </div>
                  <div>
                    <p className="text-stone-500 text-xs uppercase tracking-widest">{item.label}</p>
                    {item.href ? (
                      <a href={item.href} className="text-white font-semibold text-sm hover:text-amber-400 transition-colors cursor-pointer whitespace-nowrap">
                        {item.value}
                      </a>
                    ) : (
                      <p className="text-stone-300 text-sm font-medium">{item.value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Review badge */}
            <div className="reveal reveal-delay-2">
              <a
                href={BUSINESS.google}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-4 border border-stone-700 hover:border-amber-700/60 px-5 py-4 rounded-sm transition-all duration-300 cursor-pointer group"
              >
                <i className="ri-google-fill text-2xl text-stone-400 group-hover:text-amber-400 transition-colors" />
                <div>
                  <div className="flex items-center gap-0.5 mb-0.5">
                    {[...Array(5)].map((_, i) => (
                      <span key={i} className="w-3.5 h-3.5 flex items-center justify-center">
                        <i className="ri-star-fill text-amber-400 text-xs" />
                      </span>
                    ))}
                  </div>
                  <p className="text-stone-400 text-xs">
                    <strong className="text-white">340+</strong> Five-Star Google Reviews
                  </p>
                </div>
              </a>
            </div>
          </div>

          {/* ── Right — Form ── */}
          <div className="reveal reveal-delay-2 bg-white/[0.04] border border-stone-800 hover:border-stone-700 rounded-sm p-8 transition-colors duration-300">
            {status === "success" ? (
              <div className="text-center py-14">
                <div className="w-16 h-16 flex items-center justify-center bg-amber-700/20 border border-amber-700/40 rounded-sm mx-auto mb-5">
                  <i className="ri-check-line text-3xl text-amber-400" />
                </div>
                <h3 className="font-serif text-2xl text-white mb-3">Request Received!</h3>
                <p className="text-stone-400 text-sm mb-5">
                  We&apos;ll be in touch shortly. For immediate service:
                </p>
                <a href="tel:19734891019" className="btn-luxury bg-amber-700 hover:bg-amber-600 text-white text-xs tracking-[0.22em] uppercase font-semibold px-8 py-3.5 rounded-sm cursor-pointer inline-block">
                  Call (973) 489-1019
                </a>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-readdy-form id="free-quote-form">
                <h3 className="font-serif text-2xl text-white mb-7">Tell Us About Your Project</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-stone-400 text-xs tracking-widest uppercase mb-2">
                      Full Name <span className="text-amber-500">*</span>
                    </label>
                    <input
                      name="full_name"
                      type="text"
                      required
                      placeholder="Jane Smith"
                      className="w-full bg-stone-900 border border-stone-700 focus:border-amber-600 outline-none px-4 py-3 text-sm text-stone-200 placeholder-stone-600 rounded-sm transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-stone-400 text-xs tracking-widest uppercase mb-2">
                      Phone <span className="text-amber-500">*</span>
                    </label>
                    <input
                      name="phone"
                      type="tel"
                      required
                      placeholder="(555) 000-0000"
                      className="w-full bg-stone-900 border border-stone-700 focus:border-amber-600 outline-none px-4 py-3 text-sm text-stone-200 placeholder-stone-600 rounded-sm transition-colors"
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-stone-400 text-xs tracking-widest uppercase mb-2">
                    Email <span className="text-amber-500">*</span>
                  </label>
                  <input
                    name="email"
                    type="email"
                    required
                    placeholder="jane@email.com"
                    className="w-full bg-stone-900 border border-stone-700 focus:border-amber-600 outline-none px-4 py-3 text-sm text-stone-200 placeholder-stone-600 rounded-sm transition-colors"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-stone-400 text-xs tracking-widest uppercase mb-2">
                    Service Needed <span className="text-amber-500">*</span>
                  </label>
                  <select
                    name="service"
                    required
                    className="w-full bg-stone-900 border border-stone-700 focus:border-amber-600 outline-none px-4 py-3 text-sm text-stone-200 rounded-sm transition-colors cursor-pointer"
                  >
                    <option value="">Select a Service</option>
                    <option>Drapery Cleaning</option>
                    <option>Blind Cleaning</option>
                    <option>Carpet Cleaning</option>
                    <option>Luminette Cleaning</option>
                    <option>Silhouette Cleaning</option>
                    <option>Chandelier Cleaning</option>
                    <option>Upholstery Cleaning</option>
                    <option>Area Rug Cleaning</option>
                    <option>Multiple Services</option>
                  </select>
                </div>
                <div className="mb-4">
                  <label className="block text-stone-400 text-xs tracking-widest uppercase mb-2">
                    City / Location
                  </label>
                  <input
                    name="city"
                    type="text"
                    placeholder="e.g. Newark, NJ"
                    className="w-full bg-stone-900 border border-stone-700 focus:border-amber-600 outline-none px-4 py-3 text-sm text-stone-200 placeholder-stone-600 rounded-sm transition-colors"
                  />
                </div>
                <div className="mb-7">
                  <label className="block text-stone-400 text-xs tracking-widest uppercase mb-2">
                    Message
                  </label>
                  <textarea
                    name="message"
                    rows={3}
                    maxLength={500}
                    placeholder="Briefly describe your window treatments or project..."
                    className="w-full bg-stone-900 border border-stone-700 focus:border-amber-600 outline-none px-4 py-3 text-sm text-stone-200 placeholder-stone-600 rounded-sm transition-colors resize-none"
                  />
                </div>
                {status === "error" && (
                  <p className="text-red-400 text-xs mb-4">Something went wrong. Please call us directly at (973) 489-1019.</p>
                )}
                <button
                  type="submit"
                  disabled={status === "sending"}
                  className="w-full bg-amber-700 hover:bg-amber-600 disabled:bg-amber-900 text-white text-xs tracking-[0.25em] uppercase font-semibold py-4 rounded-sm transition-all duration-300 cursor-pointer whitespace-nowrap btn-luxury"
                >
                  {status === "sending" ? "Sending..." : "Request My Free Estimate"}
                </button>
                <p className="text-stone-600 text-xs text-center mt-4">
                  Or call us directly:{" "}
                  <a href="tel:19734891019" className="text-amber-600 hover:text-amber-400 font-semibold cursor-pointer transition-colors">
                    (973) 489-1019
                  </a>
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
